import { useState } from 'react';

import { tracking } from '@iwoca/frontend-tracking-library';
import { IconPlus } from '@iwoca/icons';
import { GetVerifiedBankAccountsResponse } from '@iwoca/lapi-client/edge';
import { Select } from '@iwoca/orion';
import { Button } from '@iwoca/orion-ui';
import { FormattedMessage, useIntl } from 'react-intl';

import styles from './BankDetailsActionDE.module.css';
import AgreeDirectDebitMandateModal from '../../../../modals/agreeDirectDebitMandateModal/AgreeDirectDebitMandateModal';

export const SelectVerifiedAccount = ({
    verifiedBankAccounts,
    triggerRefetchFundingRequirements
}: {
    verifiedBankAccounts: NonNullable<GetVerifiedBankAccountsResponse['data']>;
    triggerRefetchFundingRequirements: () => void;
}) => {
    const intl = useIntl();

    const [directDebitMandateModalMode, setDirectDebitMandateModalMode] =
        useState<null | 'verify_existing_account' | 'add_new_account'>();

    const [selectedVerifiedAccountNumber, setSelectedVerifiedAccountNumber] =
        useState(
            verifiedBankAccounts.length === 1
                ? verifiedBankAccounts[0].number
                : ''
        );
    return (
        <div
            className={styles.stepItemContainer}
            data-testid="DE-add-bank-details"
        >
            <AgreeDirectDebitMandateModal
                isModalOpen={!!directDebitMandateModalMode}
                onClose={() => {
                    setDirectDebitMandateModalMode(null);
                    triggerRefetchFundingRequirements();
                }}
                previouslyVerifiedIBAN={
                    directDebitMandateModalMode === 'verify_existing_account'
                        ? selectedVerifiedAccountNumber
                        : undefined
                }
            />
            <div>
                <div className={styles.stepMessages}>
                    <span className={styles.title}>
                        <FormattedMessage
                            id="_.funding_requirements.add_bank_details_title"
                            defaultMessage="Add your business bank details"
                        />
                    </span>
                    <span className={styles.caption}>
                        <FormattedMessage
                            id="_.funding_requirements.add_bank_details_caption"
                            defaultMessage="We’ll send the funds to this account."
                        />
                    </span>
                </div>
                <div className={styles.buttonDropDownRow}>
                    <div className={styles.chooseAccountDropdown}>
                        <Select
                            onChange={(e) => {
                                setSelectedVerifiedAccountNumber(
                                    e.target.value
                                );
                            }}
                            value={selectedVerifiedAccountNumber}
                            defaultValue={selectedVerifiedAccountNumber}
                            placeholder={intl.formatMessage({
                                id: '_.funding_requirements.add_bank_details_dropdown_message',
                                defaultMessage:
                                    'Please choose a verified bank account'
                            })}
                        >
                            {verifiedBankAccounts.map((bankAccount) => (
                                <option
                                    key={bankAccount.number}
                                    value={bankAccount.number}
                                >
                                    {intl.formatMessage(
                                        {
                                            id: '_.select_payment_method.verified_bank_account_selected_value',
                                            defaultMessage: 'Account: {number}'
                                        },
                                        {
                                            number: bankAccount.number
                                        }
                                    )}
                                </option>
                            ))}
                        </Select>
                    </div>
                    <div className={styles.button}>
                        <Button
                            variant="primary"
                            onClick={() => {
                                tracking.ampli.addBankDetailsUserSelectedVerifiedAccount();
                                setDirectDebitMandateModalMode(
                                    'verify_existing_account'
                                );
                            }}
                            disabled={!selectedVerifiedAccountNumber}
                        >
                            <FormattedMessage
                                id="_.funding_requirements.add_bank_details_open_direct_debit"
                                defaultMessage="Confirm Account"
                            />
                        </Button>
                    </div>
                </div>
                <div className={styles.addNewAccountAltButton}>
                    <Button
                        variant="tertiary"
                        onClick={() => {
                            setDirectDebitMandateModalMode('add_new_account');
                        }}
                    >
                        <FormattedMessage
                            id="_.funding_requirements.add_bank_details_open_direct_debit.new_account_alt_button"
                            defaultMessage="Add new business account"
                        />
                        <IconPlus />
                    </Button>
                </div>
                <p className={styles.footerCaption}>
                    <FormattedMessage
                        id="_.funding_requirements.add_bank_details_extra_information"
                        defaultMessage="If you select this option, you’ll need to provide verification documents. Selecting an account from the dropdown is faster."
                    />
                </p>
            </div>
        </div>
    );
};
